import DOMPurify from "dompurify";

// Configure DOMPurify for secure content sanitization with safe formatting
const purifyConfig = {
  ALLOWED_TAGS: ["b", "i", "em", "strong", "u", "br", "p", "a", "div"], // Allow safe formatting tags including div
  ALLOWED_ATTR: ["href"], // Only allow href for links
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM_IMPORT: false,
  FORBID_TAGS: [
    "script",
    "object",
    "embed",
    "form",
    "input",
    "iframe",
    "style",
  ],
  FORBID_ATTR: [
    "onclick",
    "onload",
    "onerror",
    "onmouseover",
    "onfocus",
    "onblur",
    "style",
    "class",
    "id",
  ],
  ALLOW_DATA_ATTR: false,
};

export const sanitizeContent = (content: string): string => {
  if (!content) return "";

  // Remove any potential XSS vectors and normalize whitespace
  const sanitized = DOMPurify.sanitize(content.trim(), purifyConfig);

  // Additional validation - ensure content length is within bounds
  if (sanitized.length > 10000) {
    throw new Error("Content exceeds maximum length");
  }

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /javascript:/i,
    /data:/i,
    /vbscript:/i,
    /on\w+\s*=/i,
    /<script/i,
    /eval\s*\(/i,
    /expression\s*\(/i,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(sanitized)) {
      throw new Error("Content contains potentially malicious code");
    }
  }

  return sanitized;
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export const escapeHtml = (text: string): string => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

// Enhanced file validation for uploads
export const validateFileUpload = (
  file: File
): { valid: boolean; error?: string } => {
  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return { valid: false, error: "File size exceeds 10MB limit" };
  }

  // Validate file type
  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
  ];

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: "File type not allowed" };
  }

  // Check file name for suspicious patterns
  const suspiciousPatterns = [
    /\.exe$/i,
    /\.bat$/i,
    /\.cmd$/i,
    /\.scr$/i,
    /\.js$/i,
    /\.vbs$/i,
    /\.php$/i,
    /\.\./,
    /[<>:"|?*]/,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(file.name)) {
      return { valid: false, error: "File name contains invalid characters" };
    }
  }

  return { valid: true };
};

// Sanitize AI-generated content with additional checks
export const sanitizeAIContent = (content: string): string => {
  if (!content) return "";

  // First pass through standard sanitization
  let sanitized = sanitizeContent(content);

  // Additional AI-specific patterns to check
  const aiSuspiciousPatterns = [
    /\[INST\]/gi,
    /\[\/INST\]/gi,
    /system:/gi,
    /assistant:/gi,
    /human:/gi,
    /<\|.*?\|>/g,
    /{{.*?}}/g,
  ];

  // Remove AI instruction patterns
  for (const pattern of aiSuspiciousPatterns) {
    sanitized = sanitized.replace(pattern, "");
  }

  return sanitized.trim();
};
